import { CloseIcon } from '@/components/base/Icons/RocheIconPack';
import ErrorCircleIcon from '@/storybook/icons/ErrorCircleIcon';
import InfoCircleIcon from '@/storybook/icons/InfoCircleIcon';
import SuccessCircleIcon from '@/storybook/icons/SuccessCircleIcon';
import WarningCircleIcon from '@/storybook/icons/WarningCircleIcon';
import cx from 'classnames';
import { ComponentProps, FC, JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../IconButton/IconButton';
import styles from './Alert.module.css';

export type AlertProps = ComponentProps<'div'> & {
  message: ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error';
  dismissible?: boolean;
  onClose?: VoidFunction;
  icon?: ReactNode;
  action?: ReactNode;
};

const VARIANT_ICONS: Record<NonNullable<AlertProps['variant']>, JSX.Element> = {
  info: <InfoCircleIcon />,
  success: <SuccessCircleIcon />,
  warning: <WarningCircleIcon />,
  error: <ErrorCircleIcon />,
};

const Alert: FC<AlertProps> = ({
  variant = 'info',
  icon,
  message,
  children,
  action,
  dismissible = false,
  onClose,
  className,
  ...rest
}) => {
  const { t } = useTranslation();
  const renderedIcon = icon || VARIANT_ICONS[variant];

  return (
    <div className={cx(styles.alert, styles[variant], className)} role="alert" {...rest}>
      <span className={styles.icon} data-element="icon">
        {renderedIcon}
      </span>
      <div className={styles.content} data-element="content">
        <p className={styles.message} data-element="message">
          {message}
        </p>
        {children && (
          <div className={styles.body} data-element="content">
            {children}
          </div>
        )}
      </div>
      {dismissible && (
        <div className={styles.actions} data-element="actions">
          {action}
          {dismissible && (
            <IconButton
              appearance="text"
              icon={<CloseIcon />}
              onClick={onClose}
              aria-label={t('components.alert.closeAriaLabel')}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Alert;
